/**
 * @component LoginContainer
 * @description Main login container component that handles multiple authentication methods:
 * - Standard username/password authentication
 * - Okta authentication
 * - Keycloak authentication
 *
 * Component Structure:
 * └─ LoginContainer
 *    ├─ StandardLoginForm
 *    │  └─ Slider (Pulic Announcements carousel)
 *    │  └─ LoginForm
 *    │  └─ Footer
 *    ├─ ForgotPassword
 *    ├─ ResetPasswordPage
 *    ├─ oktaLoginRedirect
 *    └─ KcLogin (Keycloak login provider)
 *       └─ ReactKeycloakProvider
 *
 * Features:
 * - Multiple authentication methods (Standard/Okta/Keycloak)
 * - Secure token management
 * - Password recovery flow
 * - Token validation
 * - Responsive design
 *
 * @example
 * // Standard initialization
 * new LoginContainer(core, options);
 *
 * @props {Object} core - OSJS Core instance
 * @props {Object} options - Configuration options
 */

import { Login as defaultLogin } from "../../osjs-client/index.js";
import OktaAuth from "@okta/okta-auth-js";
import { ReactKeycloakProvider } from "@react-keycloak/web";
import Keycloak from "keycloak-js";
// Component imports
import ForgotPassword from "./ForgotPassword.js";
import ResetPasswordPage from "./ResetPasswordPage.js";
import Slider from "./Slider.js";
// Asset imports
import "../assets/scss/login.scss";
import oxLogo from "../assets/images/Taruvi_transparent.png";
import wallpaper from "../assets/images/wallpaper.png";

export default class LoginContainer extends defaultLogin {
  constructor(core, options = {}) {
    super(core, options || {});
    this.core = core;

    this.baseurl = this.core.config("ui.url");
    this.viewurl = this.core.config("wrapper.url");
  }

  initializeKeycloak() {
    this.keycloakUrl = this.core.config("keycloak.keycloakUrl");
    this.keycloakRealm = this.core.config("keycloak.keycloakRealm");
    this.keycloakClientId = this.core.config("keycloak.keycloakClientId");
    this.keycloak = new Keycloak({
      url: this.keycloakUrl,
      realm: this.keycloakRealm,
      clientId: this.keycloakClientId,
    });
  }

  initializeOkta() {
    const oktaIssuer = this.core.config("okta.oktaIssuer");
    const oktaClientId = this.core.config("okta.oktaClientId");
    const oktaRedirectUri = this.core.config("okta.oktaRedirectUri");

    this.oktaAuth = new OktaAuth({
      issuer: oktaIssuer,
      clientId: oktaClientId,
      redirectUri: oktaRedirectUri,
    });
  }

  validateToken = async (form, type) => {
    return await (
      await fetch(this.viewurl + `auth/external/${type}`, {
        method: "post",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(form),
        credentials: "include",
      })
    ).json();
  };

  async handleOktaLogin() {
    try {
      const tokens = await this.oktaAuth.token.parseFromUrl();
      this.oktaAuth.tokenManager.setTokens(tokens.tokens);
      const accessToken = tokens.tokens.accessToken.accessToken;
      if (accessToken) {
        const form = new FormData();
        form.append("access_token", accessToken);
        let validate = await this.validateToken(form, "okta");
        if (validate.error) {
          console.error("Error from server:", validate.error);
          return;
        }

        let res = [{ result: validate.data, ref_token: validate.data.refresh_token }];
        this.emit("login:post", res);
      }
    } catch (error) {
      if (error.name === "OAuthError") {
        console.error("Okta OAuth Error:", error);
      } else if (error.name === "AuthApiError") {
        console.error("Okta Auth API Error:", error);
      } else {
        this.oktaLoginRedirect();
      }
    }
  }

  async handleKeycloakLogin(container) {
    const redirect = window.location.href.split(/#state|&code|&session_state/).length === 4;
    const { ReactDOM, React } = oxziongui;
    if (!redirect) {
      ReactDOM.createRoot(container).render(KcLogin(this.keycloak));
    } else {
      const formData = new FormData();
      formData.append("grant_type", "authorization_code");
      formData.append("client_id", this.keycloakClientId);
      formData.append("redirect_uri", this.baseurl);
      formData.append("code", window.location.href.split("&code=")[1]);

      const response = await (
        await fetch(this.keycloakUrl + "/realms/" + this.keycloakRealm + "/protocol/openid-connect/token", {
          body: new URLSearchParams(formData),
          method: "post",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        })
      ).json();

      if (response.access_token) {
        const keycloakValidateForm = new FormData();
        keycloakValidateForm.append("access_token", response.access_token);
        let keycloakValidateResponse = await this.validateToken(keycloakValidateForm, "keycloak");
        let res = [{ result: keycloakValidateResponse.data, ref_token: response.refresh_token }];
        this.emit("login:post", res);
        window.location.replace(window.location.href.split("#state=")[0]);
      }
    }
  }

  oktaLoginRedirect() {
    try {
      this.oktaAuth.token.getWithRedirect({
        responseType: ["id_token", "token"],
        scopes: ["openid", "profile", "email"],
      });
    } catch (error) {
      console.error("Error initiating Okta login:", error);
    }
  }

  //add another condition - if there is #login in the url params, then, even if okta or keycloak is enabled then we need to redirect to our login page
  //when logging in through okta and refreshing then it is going back to okta login

  async initializeAuthenticationFlow(container) {
    const authType = window.location.href.includes("#login") || window.location.href.includes("#forgotpassword") || window.location.href.includes("/register") || window.location.href.includes("resetpassword") ? null : this.core.config("authType");
    switch (authType) {
      case "okta":
        this.initializeOkta();
        await this.handleOktaLogin(container);
        break;
      case "keycloak":
        this.initializeKeycloak();
        await this.handleKeycloakLogin(container);
        break;
      default:
        break;
    }
  }

  async render() {
    const { ReactDOM, React } = oxziongui;
    const LoginContainerComponent = getLoginContainerComponent({ React, ReactDOM });

    const container = document.createElement("div");
    container.className = "reactLoginPage";
    document.body.appendChild(container);

    const root = ReactDOM.createRoot(container);
    root.render(<LoginContainerComponent core={this.core} thisObj={this} React={React} ReactDOM={ReactDOM} />);

    this.on("login:stop", () => {
      let AuthToken = this.core.make("oxzion/profile").getAuth();
      if (AuthToken) {
        root.unmount();
        container.remove(); // Also remove the DOM element
      }
    });
  }
}

const getLoginContainerComponent = ({ React, ReactDOM }) => {
  // Move React.lazy outside the component
  const Register = React.lazy(() => import("./Register.jsx"));
  const LoginForm = React.lazy(() => import("./LoginForm.jsx"));

  return class LoginContainerComponent extends React.Component {
    constructor(props) {
      super(props);
      this.core = this.props.core;
      this.thisObj = this.props.thisObj;
      this.state = {
        currentRoute: this.getInitialRoute(),
        routeProps: undefined,
      };
    }

    getInitialRoute() {
      const url = window.location.href;
      if (url.includes("#forgotpassword")) return "forgotpassword";
      if (url.includes("resetpassword")) return "resetpassword";
      if (url.includes("/register")) return "register";
      return "default";
    }

    navigateTo = (route, props = {}) => {
      this.setState({
        currentRoute: route,
        routeProps: props,
      });
    };

    createLoginContainer = () => {
      const node = document.createElement("div");
      node.className = "reactLoginPage";
      document.body.appendChild(node);
      return node;
    };

    componentDidMount() {
      this.thisObj.initializeAuthenticationFlow(this.createLoginContainer());
    }

    render() {
      const { currentRoute, routeProps } = this.state;

      switch (currentRoute) {
        case "forgotpassword":
          return <ForgotPassword core={this.core} navigateTo={this.navigateTo} routeProps={routeProps} />;

        case "resetpassword":
          const token = routeProps?.resetPasswordToken || new URLSearchParams(window.location.search).get("resetpassword");
          return <ResetPasswordPage core={this.core} resetPasswordToken={token} navigateTo={this.navigateTo} routeProps={routeProps} />;

        case "register":
          return (
            <React.Suspense fallback={<div>Loading...</div>}>
              <Register
                core={this.core}
                url={window.location.href}
                thisObj={this.thisObj}
                oxLogo={oxLogo}
                navigateTo={this.navigateTo}
                routeProps={routeProps}
              />
            </React.Suspense>
          );
        default:
          const authType = window.location.href.includes("#login") ? null : this.core.config("authType");
          if (!authType) {
            return (
              <main id="login-container" className="loginContainer row lighten-3">
                <img
                  src={wallpaper}
                  alt="EOX Vantage"
                  style={{
                    position: "absolute",
                    width: "100vw",
                    height: "135vh",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%,-50%)",
                  }}
                />
                <div className="loginSlider col-8" style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <Slider core={this.core} />
                </div>
                <React.Suspense fallback={<div>Loading...</div>}>
                  <LoginForm origin="loginPage" thisObj={this.props.thisObj} oxLogo={oxLogo} navigateTo={this.navigateTo} />
                </React.Suspense>
              </main>
            );
          }
          return null;
      }
    }
  };
};

const KcLogin = (keycloak) => {
  const { React } = oxziongui;
  const redirect = window.location.href.split(/#state|&code|&session_state/).length === 4;

  return !redirect ? (
    <ReactKeycloakProvider
      authClient={keycloak}
      initOptions={{ onLoad: "login-required", checkLoginIframe: false }} // onLoad: "login-required"
      onEvent={(event, error) => {
        console.log("onKeycloakEvent", event, error);
      }}
    ></ReactKeycloakProvider>
  ) : null;
};
